import type { DeltaPacketRecord } from '@/models/hydrate.ts';
import { EmojiReaction } from '@/models/registry.ts';
import type { BaseStore } from '@/models/stores/base.ts';
import {
  EmojiReactionAction,
  type EmojiReactionSummary,
  type EmojiReactionSummaryMap,
  type EmojiReaction as EmojiReactionType,
  EmojiReactionType as ReactionType,
} from '@/models/types.ts';
import dayjs from 'dayjs';
import {
  type IObservableArray,
  makeAutoObservable,
  observable,
  ObservableMap,
} from 'mobx';
import { v4 as uuidv4 } from 'uuid';
import { db } from '../db';

class EmojiReactionsStore
  implements BaseStore<EmojiReaction, EmojiReactionType>
{
  store: ObservableMap<string, EmojiReaction> = observable.map();
  private _empty: IObservableArray<EmojiReaction> = observable.array([], {
    deep: false,
  });

  // Indexes for different target types
  storeIndexedByDealId: ObservableMap<string, EmojiReaction[]> =
    observable.map();
  storeIndexedByCommentId: ObservableMap<string, EmojiReaction[]> =
    observable.map();
  storeIndexedByReplyId: ObservableMap<string, EmojiReaction[]> =
    observable.map();
  storeIndexedByTradeId: ObservableMap<string, EmojiReaction[]> =
    observable.map();

  private reactionTypeToIndexMap = {
    [ReactionType.Deal]: this.storeIndexedByDealId,
    [ReactionType.Comment]: this.storeIndexedByCommentId,
    [ReactionType.Reply]: this.storeIndexedByReplyId,
    [ReactionType.Trade]: this.storeIndexedByTradeId,
  };

  constructor() {
    makeAutoObservable(
      this,
      {
        store: observable.shallow,
        storeIndexedByDealId: observable.shallow,
        storeIndexedByCommentId: observable.shallow,
        storeIndexedByReplyId: observable.shallow,
        storeIndexedByTradeId: observable.shallow,
      },
      { autoBind: true },
    );
  }

  get all(): EmojiReaction[] {
    return Array.from(this.store.values());
  }

  getById(id: string): EmojiReaction | undefined {
    return this.store.get(id);
  }

  private setReactionField = <K extends keyof EmojiReactionType>(
    reaction: EmojiReaction,
    key: K,
    value: EmojiReactionType[K],
  ) => {
    (reaction as Pick<EmojiReaction, K>)[key] = value as EmojiReaction[K];
  };

  applyDeltaV2({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<EmojiReactionType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'delete' || delta.action === 'archive') {
      if (hydrateIndexedDB) {
        db.emoji_reactions.delete(delta.record_id);
      }
      this.remove(delta.record_id);
      return;
    }

    if (delta.action === 'create') {
      if (hydrateIndexedDB) {
        db.emoji_reactions.put(delta.data, delta.record_id);
      }
      this.upsert(delta.data);
      return;
    }

    if (delta.action === 'update') {
      if (hydrateIndexedDB) {
        db.emoji_reactions.update(delta.record_id, delta.data);
      }
      this.replaceOnlyUpdatedFields({ delta });
      return;
    }

    if (hydrateIndexedDB) {
      db.emoji_reactions.put(delta.data, delta.record_id);
    }
    this.upsert(delta.data);
  }

  replaceOnlyUpdatedFields({
    delta,
  }: {
    delta: DeltaPacketRecord<EmojiReactionType>;
  }) {
    const existingReaction = this.getById(delta.record_id);
    if (!existingReaction) return;

    const entries = Object.entries(delta.data) as [
      keyof EmojiReactionType,
      EmojiReactionType[keyof EmojiReactionType],
    ][];

    for (const [key, newValue] of entries) {
      if (newValue === undefined || existingReaction[key] === newValue) {
        continue;
      }

      this.setReactionField(existingReaction, key, newValue);
    }
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<EmojiReactionType>[];
    hydrateIndexedDB?: boolean;
  }) {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<EmojiReactionType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (hydrateIndexedDB) {
      db.emoji_reactions.put(delta.data, delta.record_id);
    }

    if (delta.action === 'delete') {
      this.remove(delta.record_id);
      return;
    }

    if (delta.action === 'archive') {
      this.remove(delta.record_id);
      return;
    }

    this.upsert(delta.data);
  }

  private getOrCreateCollection(
    map: ObservableMap<string, EmojiReaction[]>,
    key: string,
  ) {
    let arr = map.get(key);
    if (!arr) {
      arr = observable.array<EmojiReaction>([], { deep: false });
      map.set(key, arr);
    }
    return arr;
  }

  getByTarget(targetId: string, targetType: ReactionType): EmojiReaction[] {
    const indexMap = this.reactionTypeToIndexMap[targetType];
    if (!indexMap) return this._empty;
    return this.getOrCreateCollection(indexMap, targetId);
  }

  getByTargetUserAndEmoji(
    targetId: string,
    targetType: ReactionType,
    userId: string,
    emoji: string,
  ): EmojiReaction | undefined {
    const reactions = this.getByTarget(targetId, targetType);
    return reactions.find(
      (reaction) => reaction.user_id === userId && reaction.emoji === emoji,
    );
  }

  setOne(emojiReaction: EmojiReaction) {
    if (this.store.has(emojiReaction.id)) {
      return;
    }

    this.store.set(emojiReaction.id, emojiReaction);
    this.addToIndexes(emojiReaction);
  }

  addToIndexes(emojiReaction: EmojiReaction) {
    const { reaction_to_id: targetId, reaction_to_type: targetType } =
      emojiReaction;
    const indexMap = this.reactionTypeToIndexMap[targetType];

    this.getOrCreateCollection(indexMap, targetId).push(emojiReaction);
  }

  removeFromIndexes(emojiReaction: EmojiReaction) {
    const { reaction_to_id: targetId, reaction_to_type: targetType } =
      emojiReaction;
    const indexMap = this.reactionTypeToIndexMap[targetType];

    const removeFrom = (arr: EmojiReaction[]) => {
      const i = arr.findIndex((r) => r.id === emojiReaction.id);
      if (i >= 0) arr.splice(i, 1);
    };

    removeFrom(this.getOrCreateCollection(indexMap, targetId));
  }

  add(emojiReaction: EmojiReactionType) {
    const newReaction = new EmojiReaction(emojiReaction);
    this.setOne(newReaction);
  }

  upsert(emojiReaction: EmojiReactionType) {
    const existingReaction = this.getByTargetUserAndEmoji(
      emojiReaction.reaction_to_id,
      emojiReaction.reaction_to_type,
      emojiReaction.user_id,
      emojiReaction.emoji,
    );

    if (existingReaction) {
      this.store.delete(existingReaction.id);

      existingReaction.id = emojiReaction.id;

      this.store.set(emojiReaction.id, existingReaction);
    } else {
      this.add(emojiReaction);
    }
  }

  remove(id: EmojiReactionType['id']) {
    const reaction = this.store.get(id);
    if (!reaction) return;

    this.store.delete(id);
    this.removeFromIndexes(reaction);
    db.emoji_reactions.delete(id);
  }

  toggle(
    targetId: string,
    targetType: ReactionType,
    userId: string,
    emoji: string,
  ): { action: EmojiReactionAction; id: string } {
    const existingReaction = this.getByTargetUserAndEmoji(
      targetId,
      targetType,
      userId,
      emoji,
    );
    if (existingReaction) {
      this.remove(existingReaction.id);
      return { action: EmojiReactionAction.Remove, id: existingReaction.id };
    } else {
      const id = uuidv4();
      this.add({
        id,
        reaction_to_id: targetId,
        reaction_to_type: targetType,
        created_at: dayjs().toISOString(),
        user_id: userId,
        emoji,
      });
      return { action: EmojiReactionAction.Add, id };
    }
  }

  getEmojiSummaryForTarget(
    targetId: string,
    targetType: ReactionType,
    currentUserId: string,
  ): EmojiReactionSummaryMap {
    const emojiReactions = this.getByTarget(targetId, targetType);
    const map: EmojiReactionSummaryMap = {};

    for (const emojiReaction of emojiReactions) {
      const summary: EmojiReactionSummary = map[emojiReaction.emoji] ?? {
        emoji: emojiReaction.emoji,
        hasUserReacted: false,
        usersReacted: [],
      };
      summary.hasUserReacted ||= emojiReaction.user_id === currentUserId;
      summary.usersReacted.push(emojiReaction.created_by?.username ?? 'anon');
      map[emojiReaction.emoji] = summary;
    }
    return map;
  }
}

export const emojiReactionsStore = new EmojiReactionsStore();
